import { AssessmentType } from "score_performance";
import { createEmitter } from "ts_utils";
import { StringUtils } from "ts_utils";
import { SfxPlayer } from "../audio/SfxPlayer";
import { ContestManager } from "../data/ContestManager";
import { ContestEntryUpdateDTO } from "../data/dtos";
import { SongDO } from "../data/SongDO";
import { StudentManager } from "../data/StudentManager";
import { Services } from "../services/Services";
import { AppSettings } from "../settings/AppSettings";
import { pixiAppHelper } from "../ui/pixi_apps";
import { RAButton } from "../ui/RAButton";
import { ScoreMessageBox } from "../ui/ScoreMessageBox";
import { RAPlayer, RAPlayerEvent } from "./RAPlayer";
import { RAPlayerSettingsWindow } from "./RAPlayerSettingsWindow";

export class RAControls {
	private _ui:HTMLDivElement;
	
	private _head:HTMLDivElement;
	private _fingeringsDiv:HTMLDivElement;
	
	private _playBtnImg:HTMLImageElement;
	private _playBtn:RAButton;
	private _songMenuBtn:RAButton;
	private _settingsBtn:RAButton;
	private _upTempoBtn:RAButton;
	private _downTempoBtn:RAButton;
	
	private _player:RAPlayer;
	
	private _resizeTimerId:number;
	
	private _isFirstSongLoad:boolean = true;
	
	public get event() { return this._emitter.event; }
	private _emitter = createEmitter<RAControlsEvent>();

	constructor(ui:HTMLDivElement, player:RAPlayer) {
		this._ui = ui;
		this._player = player;
		
		this._init();
	}
	
	public async openSong(songDO:SongDO):Promise<void> {
		if (this._isFirstSongLoad){
			pixiAppHelper.resizeToParent();
			await SfxPlayer.loadSounds();
			this._isFirstSongLoad = false;
		}
		await this._player.openSong(songDO);
		this._updateTitle();
		this._updateAssessmentMessage();
		this._setMode(ControlsMode.stopped);
	}
	
	public closeSong():void {
		this._setMode(ControlsMode.disabled);
		
		this._player.closeSong();
		this._hideScorePercentage();
		this._player.tempoRatio = 1.0;
		this._updateTempoDisplay();
	}
	
	public displayMessage(message:string):void {
		let msgRow = this._ui.querySelector("#p-head-messageRow") as
			HTMLDivElement;
		msgRow.style.display = "initial";
		let msgDiv = msgRow.querySelector("#p-head-message") as
			HTMLDivElement;
		msgDiv.innerText = message;
	}
	
	public hideMessage():void {
		let msgRow = this._ui.querySelector("#p-head-messageRow") as
			HTMLDivElement;
		msgRow.style.display = "none";
	}
	
	private _init():void {
		this._draw();
		this._addListeners();
	}

	private _setMode(mode:number):void {
		if (mode === ControlsMode.disabled){
			this._settingsBtn.enabled = false;
			this._songMenuBtn.enabled = false;
			this._playBtn.enabled = false;
			this._upTempoBtn.enabled = false;
			this._downTempoBtn.enabled = false;
			this._playBtnImg.src = AppSettings.resourcesPath +
				"images/playerScreen/playBtn.png";
		}
		else if (mode === ControlsMode.stopped){
			this._settingsBtn.enabled = true;
			this._songMenuBtn.enabled = true;
			this._playBtn.enabled = true;
			this._upTempoBtn.enabled = true;
			this._downTempoBtn.enabled = true;
			this._playBtnImg.src = AppSettings.resourcesPath +
				"images/playerScreen/playBtn.png";
		}
		else if (mode === ControlsMode.playing){
			this._settingsBtn.enabled = false;
			this._songMenuBtn.enabled = false;
			this._playBtn.enabled = true;
			this._upTempoBtn.enabled = false;
			this._downTempoBtn.enabled = false;
			this._playBtnImg.src = AppSettings.resourcesPath +
				"images/playerScreen/stopBtn.png";
		}
	}

	private _draw():void {
		let qs = this._ui.querySelector.bind(this._ui);
		this._head = qs("#p-head");
		
		this._playBtnImg = qs('#p-head-playBtn img');
		this._playBtn = new RAButton(qs('#p-head-playBtn'));
		this._upTempoBtn = new RAButton(qs('#p-head-tempoUpBtn'));
		this._downTempoBtn = new RAButton(qs('#p-head-tempoDownBtn'));
		this._songMenuBtn = new RAButton(qs('#p-head-songsBtn'));
		this._settingsBtn = new RAButton(qs('#p-head-settingsBtn'));
		
		this._fingeringsDiv = this._ui.querySelector("#_sp-fingeringsDiv");
		
		this._onAppResize();
	}

	
	
	private _displayScorePercentage(percentage:number):void {
		let div = document.querySelector("#percentageDiv") as HTMLDivElement;
		div.innerHTML = (`${percentage}%`);
		div.style.display = "block";
	}
	
	public _hideScorePercentage():void {
		let div = document.querySelector("#percentageDiv") as HTMLDivElement;
		div.style.display = "none";
	}

	private _onTempoUpBtnClick = (e:MouseEvent):void => {
		this._player.tempoRatio += 0.1;
		if (this._player.tempoRatio > 4.0){
			this._player.tempoRatio = 4.0;
		}
		this._updateTempoDisplay();
	}

	private _onTempoDownBtnClick = (e:MouseEvent):void => {
		this._player.tempoRatio -= 0.1;
		if (this._player.tempoRatio < 0.1){
			this._player.tempoRatio = 0.1;
		}
		this._updateTempoDisplay();
	}

	private _updateTempoDisplay():void {
		let tempoDiv = this._ui.querySelector('#p-head-tempoText');
		let tempo = Math.round(this._player.tempoRatio * 100)
		tempoDiv.innerHTML = `Tempo: ${tempo}%`;
	}

	private _onSettingsBtnClick = async (e:MouseEvent):Promise<void> => {
		await new RAPlayerSettingsWindow(this._player).display();
	}

	private _onSongMenuBtnClick = (e:MouseEvent):void => {
		let ev = new RAControlsEvent();
		ev.type = RAControlsEventType.mainMenuRequest;
		this._emitter.emit(ev);
	}

	private _onPlayBtnClick = async (e:MouseEvent):Promise<void> => {
		if (this._player.isPlaying){
			this._player.stop();
			await this._handlePlaybackStopped(false);
		}
		else {
			this._setMode(ControlsMode.playing);
			await this._player.renderMusic();
			this._player.play();
		}
	}
	
	private _onPlaybackComplete = async (e:RAPlayerEvent):Promise<void> => {
		await this._handlePlaybackStopped(true);
	}
	
	private async _handlePlaybackStopped(playbackComplete:boolean)
			:Promise<void> {
		this._setMode(ControlsMode.stopped);
		if (playbackComplete){
			let results = this._player.getAssessmentResults();
			let percentage = Math.round(results.percentage);
			
			this._displayScorePercentage(percentage);
			
			if (percentage >= 90){
				SfxPlayer.playSound("clap3");
			}
			let points = results.notesCorrect * 10 - results.notesExtra * 5;
			if (StudentManager.currentStudent != null && percentage > 0 
					&& points > 0){
				
				//update student's personal best
				let student = StudentManager.currentStudent;
				student.updateScore(this._player.songDO.libId, percentage);
				StudentManager.saveToStorage(true);
				
				//submit points for team, if it exists.
				if (student.team != null){
					let team = student.team;
					let multiplier = (team.size < 500)
						? Math.pow(500 / team.size, 0.4)
						: 1;
					points = Math.floor(points * multiplier);
					new ScoreMessageBox(points);
				
					let dto = new ContestEntryUpdateDTO();
					dto.dateString = ContestManager.getCompetitionId();
					dto.points = points;
					dto.teamId = team.id;
					await Services.updateScore(dto);
				}
			}
			this._player.scrollToTop();
		}
	}
	
	private _updateTitle():void {
		let title = this._head.querySelector("#p-head-title") as
			HTMLHeadElement;
		title.innerText = this._player.songDO.title;
	}
	
	private _updateAssessmentMessage():void {
		switch (this._player.assessmentType){
			case AssessmentType.pitch: {
				this.displayMessage("Play this song with your recorder to " +
					"earn points.");
				break;
			}
			case AssessmentType.rhythmKeyboard: {
				this.displayMessage("Tap keys on your keyboard to perform " +
					"the correct rhythms");
				break;
			}
		}
	}
	
	private _onAppResize = async ():Promise<void> => {
		if (this._resizeTimerId != null){
			window.clearTimeout(this._resizeTimerId)
		}
		this._resizeTimerId = window.setTimeout(this._handleAppResize, 1000);
	}
	
	private _handleAppResize = async ():Promise<void> => {
		if (this._player.scoreLoaded){
			if (this._player.isPlaying){
				//don't resize during playback since this will change the
				//VisualScore. We always resize just before and just after
				//playback.
				return;
				// this._player.stop();
			}
			this._player.clearAssessmentVisuals();
			await this._player.renderMusic();
		}
		//resize fingerings div
		let height:number = document.body.clientHeight;
		this._fingeringsDiv.style.width = `${Math.min(height * 0.125, 80)}px`;
	}
	
	private _addListeners():void {
		this._upTempoBtn.click.on(this._onTempoUpBtnClick);
		this._downTempoBtn.click.on(this._onTempoDownBtnClick);
		this._settingsBtn.click.on(this._onSettingsBtnClick);
		this._songMenuBtn.click.on(this._onSongMenuBtnClick);
		this._playBtn.click.on(this._onPlayBtnClick);
		
		this._player.event.on(this._onPlaybackComplete);
		
		pixiAppHelper.app.renderer.addListener("resize", this._onAppResize);
	}
}

enum ControlsMode {
	disabled,
	stopped,
	playing
}

export class RAControlsEvent {
	public type:RAControlsEventType;
}

export enum RAControlsEventType {
	mainMenuRequest
}